import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, CircleStop, Pause, Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { getActiveMetaPages } from '@/services/api/connections';
import {
    disablePageDebug,
    enablePageDebug,
    getPageDebugEvents,
    getPageDebugSnapshot,
    type DebugEvent,
    type DebugStatus,
    type PageDebugSnapshot,
} from '@/services/api/page-debug';

const POLL_MS = 2000;

const STEP_LABEL: Record<string, string> = {
    webhook_recebido: 'Webhook',
    worker_recebeu: 'Worker',
    dedup: 'Duplicado',
    sem_texto: 'Sem texto',
    pagina: 'Página',
    lead: 'Lead',
    gatilho: 'Gatilho',
    postback: 'Postback',
    token: 'Token',
    flow: 'Funil',
    node: 'Node',
    meta_envio: 'Meta',
    delay: 'Delay',
};

const TONE: Record<DebugStatus, { dot: string; badge: string; row: string }> = {
    ok: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', row: '' },
    info: { dot: 'bg-sky-500', badge: 'bg-sky-50 text-sky-700 border-sky-200', row: '' },
    warn: { dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-800 border-amber-200', row: 'bg-amber-50/40' },
    error: { dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200', row: 'bg-red-50/50' },
};

const timeFmt = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', second: '2-digit' });

function fmtTs(ts: number) {
    const ms = String(Math.floor((ts % 1) * 1000)).padStart(3, '0');
    return `${timeFmt.format(new Date(ts * 1000))}.${ms}`;
}

function fmtAgo(seconds: number) {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1).replace('.', ',')} h`;
    return `${Math.round(seconds / 86400)} dias`;
}

function fmtClock(s: number) {
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function PageDebug() {
    const queryClient = useQueryClient();
    const [pageId, setPageId] = useState<string | undefined>(() => new URLSearchParams(window.location.search).get('page') ?? undefined);
    const [events, setEvents] = useState<DebugEvent[]>([]);
    const [senderFilter, setSenderFilter] = useState<string>('');
    const [paused, setPaused] = useState(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [expiresIn, setExpiresIn] = useState(0);
    const lastId = useRef<string | null>(null);

    const { data: pages = [] } = useQuery({
        queryKey: ['active-meta-pages'],
        queryFn: getActiveMetaPages,
        staleTime: 1000 * 60 * 5,
    });

    const pageOptions = useMemo(
        () => (pages as { page_id: string; page_name?: string }[]).map((p) => ({
            value: p.page_id,
            label: `${p.page_name ?? p.page_id} · ${p.page_id}`,
        })),
        [pages],
    );

    const snapshot = useQuery({
        queryKey: ['page-debug-snapshot', pageId],
        queryFn: () => getPageDebugSnapshot(pageId!),
        enabled: !!pageId,
        refetchInterval: 30_000,
    });

    useEffect(() => {
        setEvents([]);
        setSenderFilter('');
        setExpanded(new Set());
        lastId.current = null;
        if (pageId) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', pageId);
            window.history.replaceState(null, '', url);
        }
    }, [pageId]);

    useEffect(() => {
        if (snapshot.data) setExpiresIn(snapshot.data.debug.expires_in);
    }, [snapshot.data]);

    useEffect(() => {
        if (expiresIn <= 0) return;
        const t = setInterval(() => setExpiresIn((v) => Math.max(0, v - 1)), 1000);
        return () => clearInterval(t);
    }, [expiresIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

    const debugOn = expiresIn > 0;

    useEffect(() => {
        if (!pageId || paused) return;
        let stop = false;
        const tick = async () => {
            try {
                const res = await getPageDebugEvents(pageId, lastId.current);
                if (stop) return;
                if (res.last_id) lastId.current = res.last_id;
                if (res.events.length) setEvents((prev) => [...prev, ...res.events].slice(-1000));
                setExpiresIn(res.debug.expires_in);
            } catch {
                /* próxima volta tenta de novo */
            }
        };
        tick();
        const t = setInterval(tick, POLL_MS);
        return () => { stop = true; clearInterval(t); };
    }, [pageId, paused]);

    const toggleDebug = useMutation({
        mutationFn: () => (debugOn ? disablePageDebug(pageId!) : enablePageDebug(pageId!, 30)),
        onSuccess: (res) => {
            setExpiresIn(res.expires_in);
            queryClient.invalidateQueries({ queryKey: ['page-debug-snapshot', pageId] });
        },
    });

    const senders = useMemo(() => {
        const map = new Map<string, { lead?: string; count: number }>();
        for (const e of events) {
            if (!e.sender_id) continue;
            const cur = map.get(e.sender_id) ?? { count: 0 };
            cur.count += 1;
            const name = e.data?.lead_name as string | undefined;
            if (name) cur.lead = name;
            map.set(e.sender_id, cur);
        }
        return [...map.entries()];
    }, [events]);

    const visible = useMemo(
        () => [...events].filter((e) => !senderFilter || e.sender_id === senderFilter).reverse(),
        [events, senderFilter],
    );

    const toggleRow = (id: string) => setExpanded((prev) => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    return (
        <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2"><Bug className="w-6 h-6 text-gray-500" />Debug de página</h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Ligue o debug, mande mensagem para a página pelo Messenger e acompanhe cada etapa: se o Meta entregou o webhook,
                        qual gatilho casou (ou por que nenhum casou), qual node rodou e o que o Meta respondeu no envio.
                    </p>
                </div>
                <div className="w-full sm:w-[420px]">
                    <SearchableSelect
                        value={pageId}
                        onValueChange={setPageId}
                        options={pageOptions}
                        placeholder="Escolha a página (nome ou ID)"
                        searchPlaceholder="Buscar por nome ou page_id…"
                    />
                </div>
            </div>

            {!pageId ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
                    Escolha uma página para ver o estado dela e ligar o debug.
                </div>
            ) : (
                <>
                    <Snapshot data={snapshot.data} loading={snapshot.isLoading} error={snapshot.isError} />

                    <div className="rounded-xl border border-gray-200 bg-white">
                        <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-gray-100">
                            <Button
                                size="sm"
                                onClick={() => toggleDebug.mutate()}
                                disabled={toggleDebug.isPending}
                                className={debugOn ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                            >
                                {debugOn ? <><CircleStop className="w-4 h-4 mr-1" />Desligar debug</> : <><Play className="w-4 h-4 mr-1" />Ligar debug (30 min)</>}
                            </Button>
                            {debugOn ? (
                                <span className="flex items-center gap-2 text-sm text-gray-700">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                    Gravando · desliga em {fmtClock(expiresIn)}
                                </span>
                            ) : (
                                <span className="text-sm text-gray-500">Desligado — nada é gravado para esta página.</span>
                            )}

                            <div className="ml-auto flex items-center gap-2">
                                <select
                                    value={senderFilter}
                                    onChange={(e) => setSenderFilter(e.target.value)}
                                    className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm"
                                >
                                    <option value="">Todos os remetentes ({senders.length})</option>
                                    {senders.map(([id, s]) => (
                                        <option key={id} value={id}>{s.lead ? `${s.lead} · ` : ''}{id} ({s.count})</option>
                                    ))}
                                </select>
                                <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)}>
                                    {paused ? <><Play className="w-4 h-4 mr-1" />Retomar</> : <><Pause className="w-4 h-4 mr-1" />Pausar</>}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => { setEvents([]); setExpanded(new Set()); }}>
                                    <Trash2 className="w-4 h-4 mr-1" />Limpar tela
                                </Button>
                            </div>
                        </div>

                        {visible.length === 0 ? (
                            <div className="p-10 text-center text-sm text-gray-500">
                                {debugOn
                                    ? 'Aguardando eventos… mande uma mensagem para a página agora. Se nada aparecer em ~10s, o Meta não está entregando o webhook desta página para nenhum app nosso.'
                                    : 'Ligue o debug e interaja com a página para ver os eventos aqui.'}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 max-h-[65vh] overflow-y-auto">
                                {visible.map((e) => {
                                    const tone = TONE[e.status] ?? TONE.info;
                                    const open = expanded.has(e.id);
                                    const hasData = e.data && Object.keys(e.data).length > 0;
                                    return (
                                        <div key={e.id} className={`px-4 py-2 text-sm ${tone.row}`}>
                                            <button
                                                type="button"
                                                className="w-full flex items-start gap-3 text-left"
                                                onClick={() => hasData && toggleRow(e.id)}
                                            >
                                                <span className="font-mono text-xs text-gray-500 pt-0.5 w-[92px] shrink-0">{fmtTs(e.ts)}</span>
                                                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${tone.dot}`} />
                                                <span className={`text-[11px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 w-[82px] text-center ${tone.badge}`}>
                                                    {STEP_LABEL[e.step] ?? e.step}
                                                </span>
                                                <span className="flex-1 min-w-0">
                                                    <span className="text-gray-900">{e.message}</span>
                                                    {typeof e.data?.diagnostico === 'string' && e.data.diagnostico && (
                                                        <span className="block text-xs text-red-700 mt-0.5">{e.data.diagnostico}</span>
                                                    )}
                                                    <span className="block text-[11px] text-gray-400 mt-0.5">
                                                        {[
                                                            e.sender_id && `PSID ${e.sender_id}`,
                                                            e.lead_id && `lead #${e.lead_id}`,
                                                            e.funnel_id && `funil #${e.funnel_id}`,
                                                            e.node_id && `node #${e.node_id}`,
                                                            e.data?.app_id != null && `app #${String(e.data.app_id)}`,
                                                        ].filter(Boolean).join(' · ')}
                                                    </span>
                                                </span>
                                                {hasData && <span className="text-xs text-gray-400 shrink-0">{open ? '▲' : '▼'}</span>}
                                            </button>
                                            {open && (
                                                <pre className="mt-2 ml-[110px] text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">
                                                    {JSON.stringify(e.data, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function Snapshot({ data, loading, error }: { data?: PageDebugSnapshot; loading: boolean; error: boolean }) {
    if (loading) return <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />;
    if (error || !data) return <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">Não foi possível carregar a página.</div>;

    const page = data.page[0];
    const now = data.server_time;
    const lastAgo = data.webhook.last_ts ? now - data.webhook.last_ts : null;
    const groupApp = new Set(data.app_groups.filter((g) => g.is_active).map((g) => g.app_id));
    const activeFunnels = data.funnels.filter((f) => f.is_active && f.link_active && !f.outro_dono);

    return (
        <div className="grid gap-4 lg:grid-cols-4">
            <Card title="Página">
                <div className="font-semibold text-gray-900">{page.page_name}</div>
                <div className="text-xs text-gray-500 font-mono">{page.page_id}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                    <Pill ok={!!page.is_active}>{page.is_active ? 'Ativa' : 'Inativa'}</Pill>
                    <Pill ok={page.token_status === 'ok'}>token {page.token_status ?? '—'}</Pill>
                    {data.page.length > 1 && <Pill ok={false}>{data.page.length} cadastros com este ID</Pill>}
                </div>
            </Card>

            <Card title="Webhook do Meta">
                {data.webhook.error ? (
                    <div className="text-sm text-red-700">{data.webhook.error}</div>
                ) : lastAgo == null ? (
                    <div className="text-sm text-red-700 font-medium">Nenhum webhook recebido nas últimas 24h</div>
                ) : (
                    <>
                        <div className={`text-lg font-bold ${lastAgo > 3600 ? 'text-amber-700' : 'text-gray-900'}`}>há {fmtAgo(lastAgo)}</div>
                        <div className="text-xs text-gray-500">último: {data.webhook.last_event_type ?? '—'} · {data.webhook.count_last_5min ?? 0} nos últimos 5 min</div>
                    </>
                )}
            </Card>

            <Card title={`Tokens por app (${data.tokens.length})`}>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                    {data.tokens.length === 0 && <div className="text-sm text-red-700">Nenhum token cadastrado</div>}
                    {data.tokens.map((t) => {
                        const alive = !!t.is_active && !!t.app_active && (t.token_status ?? 'ok') === 'ok';
                        const usable = alive && (t.purpose === 'funnel' || t.purpose === 'both');
                        return (
                            <div key={t.app_id} className="flex items-center gap-2 text-xs">
                                <span className={`w-2 h-2 rounded-full ${alive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                <span className="font-medium truncate">#{t.app_id} {t.app_name}</span>
                                <span className="text-gray-400">{t.purpose}</span>
                                {groupApp.has(t.app_id) && <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 rounded px-1">GRUPO</span>}
                                {!usable && alive && <span className="text-[10px] text-amber-700">não responde funil</span>}
                                {!alive && <span className="text-[10px] text-red-700">{t.token_status ?? 'inativo'}</span>}
                            </div>
                        );
                    })}
                </div>
            </Card>

            <Card title={`Funis vinculados (${activeFunnels.length} ativos de ${data.funnels.length})`}>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {data.funnels.length === 0 && <div className="text-sm text-red-700">Página sem funil — nenhuma mensagem vai ser respondida</div>}
                    {data.funnels.map((f) => {
                        const ok = f.is_active && f.link_active && !f.outro_dono;
                        return (
                            <div key={f.funnel_id} className={`text-xs ${ok ? '' : 'opacity-60'}`}>
                                <div className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                    <span className="font-medium truncate">#{f.funnel_id} {f.nome}</span>
                                    {!f.is_active && <span className="text-gray-500">inativo</span>}
                                    {!f.link_active && <span className="text-gray-500">vínculo desligado</span>}
                                    {f.outro_dono && <span className="text-gray-500">outro dono</span>}
                                </div>
                                {f.gatilhos.length > 0 && (
                                    <div className="pl-3.5 text-gray-500 truncate" title={f.gatilhos.map((g) => g.value).join(' · ')}>
                                        {f.gatilhos.map((g) => g.value).join(' · ')}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Card>
        </div>
    );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
                {title}
            </div>
            {children}
        </div>
    );
}

function Pill({ ok, children }: { ok: boolean; children: ReactNode }) {
    return (
        <span className={`text-[11px] font-medium rounded border px-1.5 py-0.5 ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            {children}
        </span>
    );
}

